import jsPDF from "jspdf";
import QRCode from "qrcode";
import autoTable from "jspdf-autotable";

/**
 * Advanced NIN Slip PDF Generator
 *
 * This utility generates PDF slips for advanced NIN verification results.
 * It uses jsPDF with autoTable to create a clean table-based PDF with all
 * the advanced NIN data fields.
 */

interface AdvancedNINVerificationData {
  nin: string;
  lastName: string;
  firstName: string;
  middleName: string;
  gender: string;
  dateOfBirth?: string;
  image?: string;
  birthLga?: string;
  birthState?: string;
  trackingId?: string;
  residenceAddress?: string;
  residenceLga?: string;
  residenceState?: string;
  residenceTown?: string;
}

export const generateAdvancedNINSlipPDF = async (
  data: AdvancedNINVerificationData
): Promise<void> => {
  // Create A4 PDF in portrait mode
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  // Constants for A4 page
  const PAGE_WIDTH = 210; // A4 width in mm
  const PAGE_HEIGHT = 297; // A4 height in mm
  const MARGIN = 20; // Margin on all sides
  const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

  // Helper functions
  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString) return "Not Provided";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatNIN = (nin: string) => {
    if (nin.length === 11) {
      return `${nin.slice(0, 4)}${nin.slice(4, 7)}${nin.slice(7, 11)}`;
    }
    return nin;
  };

  // Pre-format data
  const formattedDOB = formatDateOfBirth(data.dateOfBirth);
  const formattedNIN = formatNIN(data.nin);
  const fullName =
    `${data.firstName} ${data.middleName} ${data.lastName}`.trim();

  // Prepare QR code data
  const qrData = {
    nin: data.nin,
    name: fullName,
    dob: data.dateOfBirth || "",
    gender: data.gender,
    address: data.residenceAddress || "",
    trackingId: data.trackingId || "",
  };

  try {
    // Add header
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("ADVANCED NIN VERIFICATION SLIP", PAGE_WIDTH / 2, 30, {
      align: "center",
    });

    // Add user photo if available - centered at top
    if (data.image) {
      try {
        let imageData = data.image;
        if (!imageData.startsWith("data:")) {
          imageData = `data:image/jpeg;base64,${data.image}`;
        }

        // Calculate position for centered circular photo
        const photoSize = 40; // Diameter of the circle
        const photoX = (PAGE_WIDTH - photoSize) / 2; // Center horizontally
        const photoY = 40; // Position below title

        // Add square photo
        doc.addImage(imageData, "JPEG", photoX, photoY, photoSize, photoSize);
      } catch (error) {
        console.log("Could not process user image for PDF");
      }
    }

    // Prepare table data
    const tableData = [
      ["NIN Number", formattedNIN],
      ["Full Name", fullName],
      ["Gender", data.gender?.toUpperCase() || "Not Provided"],
      ["Date of Birth", formattedDOB],
    ];

    // Add birth location if available
    if (data.birthLga || data.birthState) {
      const birthLocation = [data.birthLga, data.birthState]
        .filter(Boolean)
        .join(", ");
      tableData.push(["Birth Location", birthLocation || "Not Provided"]);
    }

    // Add residence information
    if (data.residenceAddress) {
      tableData.push(["Residence Address", data.residenceAddress]);
    }

    if (data.residenceLga || data.residenceState || data.residenceTown) {
      const residenceLocation = [
        data.residenceTown,
        data.residenceLga,
        data.residenceState,
      ]
        .filter(Boolean)
        .join(", ");
      tableData.push([
        "Residence Location",
        residenceLocation || "Not Provided",
      ]);
    }

    // Add tracking ID if available
    if (data.trackingId) {
      tableData.push(["Tracking ID", data.trackingId]);
    }

    // Add verification status
    tableData.push(["Verification Status", "VERIFIED"]);
    tableData.push(["Slip Type", "ADVANCED SLIP"]);

    // Generate and add table
    autoTable(doc, {
      startY: data.image ? 90 : 60, // Start below photo if available
      margin: { left: MARGIN, right: MARGIN },
      head: [["Field", "Value"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [255, 140, 0], // Orange color for header
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 12,
      },
      bodyStyles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: CONTENT_WIDTH * 0.4, fontStyle: "bold" },
        1: { cellWidth: CONTENT_WIDTH * 0.6 },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
    });

    // Get the final Y position after the table
    const finalY = (doc as any).lastAutoTable?.finalY || 200;

    // Add disclaimer
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 0, 0); // Dark red color
    const disclaimerText = [
      "This is a Verification Slip generated under aggregator license.",
      "It is not an official NIMC slip. For verification purposes only.",
    ];

    disclaimerText.forEach((line, index) => {
      doc.text(line, MARGIN, finalY + 20 + index * 5);
    });

    // Add QR code
    try {
      const qrCodeDataURL = await QRCode.toDataURL(JSON.stringify(qrData), {
        width: 200,
        margin: 1,
        color: { dark: "#000000", light: "#FFFFFF" },
      });

      const qrX = PAGE_WIDTH - MARGIN - 30;
      const qrY = finalY + 10;
      const qrSize = 25;

      doc.addImage(qrCodeDataURL, "PNG", qrX, qrY, qrSize, qrSize);
    } catch (error) {
      console.warn("Failed to generate QR code:", error);
    }

    // Add footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()}`,
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - 15,
      { align: "center" }
    );

    // Save PDF
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Advanced_NIN_Slip_${data.nin}_${timestamp}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error("Error generating advanced NIN slip PDF:", error);
    throw error;
  }
};
